import React from 'react';
import { Info, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';

type Tone = 'info' | 'success' | 'warning' | 'error';

export interface TaskyAlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: Tone;
  icon?: React.ReactNode;
  title?: React.ReactNode;
}

const toneClass: Record<Tone, { bg: string; icon: React.ReactNode; text: string }> = {
  info: {
    bg: 'bg-primary/10',
    icon: <Info className="w-5 h-5 text-primary" />,
    text: 'text-dark',
  },
  success: {
    bg: 'bg-success/10',
    icon: <CheckCircle2 className="w-5 h-5 text-success" />,
    text: 'text-dark',
  },
  warning: {
    bg: 'bg-warning/10',
    icon: <AlertTriangle className="w-5 h-5 text-warning" />,
    text: 'text-dark',
  },
  error: {
    bg: 'bg-destructive/10',
    icon: <XCircle className="w-5 h-5 text-destructive" />,
    text: 'text-dark',
  },
};

export const TaskyAlert = React.forwardRef<HTMLDivElement, TaskyAlertProps>(
  ({ className, tone = 'info', icon, title, children, ...rest }, ref) => {
    const t = toneClass[tone];
    return (
      <div
        ref={ref}
        role="alert"
        {...rest}
        className={cn('rounded-tasky-md p-4 flex items-start gap-3', t.bg, t.text, className)}
      >
        <div className="shrink-0 mt-0.5">{icon ?? t.icon}</div>
        <div className="text-sm space-y-0.5 min-w-0">
          {title && <p className="font-semibold">{title}</p>}
          <div>{children}</div>
        </div>
      </div>
    );
  },
);
TaskyAlert.displayName = 'TaskyAlert';
